import { getJson, postJson } from '@/lib/apiClient';
import { tokenStore } from '@/features/auth/tokenStore';
import { config } from '@/lib/config';
import type { ChatMessage, ChatQueryResponse, ChatTurn } from '@/features/chat/types';

interface Wrapped<T> {
  data: T;
}

export interface StreamEvent {
  token?: string;
  done?: boolean;
  sources?: string[];
  request_id?: string;
  error?: string;
}

export const chatApi = {
  query: (question: string, history: ChatTurn[]): Promise<ChatQueryResponse> =>
    postJson<ChatQueryResponse>('/chat/query', { question, history }),

  save: (
    question: string,
    answer: string,
    sources: string[],
    request_id: string | null,
  ): Promise<void> =>
    postJson<void>('/chat/save', { question, answer, sources, request_id }),

  history: (): Promise<ChatMessage[]> =>
    getJson<Wrapped<ChatMessage[]>>('/chat/history').then((r) => r.data),

  /**
   * Open a streaming SSE connection for a chat query.
   * Returns an async generator that yields parsed StreamEvent objects.
   */
  async *stream(question: string, history: ChatTurn[]): AsyncGenerator<StreamEvent> {
    const token = tokenStore.get();
    const response = await fetch(`${config.apiBaseUrl}/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify({ question, history }),
    });

    if (!response.ok || !response.body) {
      throw new Error(`Stream failed: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;
          try {
            yield JSON.parse(raw) as StreamEvent;
          } catch {
            // malformed chunk — skip
          }
        }
      }
    } finally {
      reader.cancel();
    }
  },
};

export const chatKeys = {
  history: () => ['chat', 'history'] as const,
};
